/**
 * DocumentLayoutPicker.tsx — C6 (docs/refactor/LAYOUTY-POPRAWKI-PO-SMOKE.md).
 *
 * Dropdown wyboru layoutu formularza NAD CAŁYM formularzem, w pasku akcji
 * nagłówka strony detalu (7 stron dokumentów systemowych: umowy, pisma,
 * dokumenty handlowe/wewnętrzne/specjalne, faktury zakupu/sprzedaży). NIE mylić z
 * `components/modules/LayoutSwitcher.tsx` (dropdown w builderze F12.N —
 * inny kontekst, inny komponent, celowo nienadpisywany).
 *
 * Stan wyboru jest SESYJNY i żyje w stronie-callerze (`useState<string|null>`),
 * resetowany przy zmianie rekordu — ten komponent jest czysto prezentacyjny +
 * fetch listy.
 *
 * Źródło danych: `GET /system-modules/:slug/layouts?type=form` — pobierane
 * RAZ przez `useSystemModuleFormLayouts` (React Query, klucz
 * `['system-module-form-layouts', slug]`). Strona wywołuje TEN SAM hook żeby
 * rozwiązać `layoutConfig` wybranej pozycji (współdzielony cache, zero
 * dodatkowego zapytania sieciowego — "fetch raz").
 *
 * Filtr produktowy (TO NIE JEST SECURITY — layouty to kuracja prezentacji,
 * nie ACL; wartości pól dodatkowych przychodzą z odpowiedzią GET rekordu
 * (`extValues`) pod uprawnieniem do odczytu rekordu — layout ich nie odsłania):
 * pokazujemy layouty uniwersalne (`roleIds` puste) + przypisane do roli
 * użytkownika + zawsze domyślny (`isDefault`); super-user widzi wszystko.
 * Dropdown renderuje się TYLKO gdy po filtrze zostanie >1 pozycja (przy ≤1
 * nie ma czego przełączać).
 *
 * Brak bramki uprawnienia: endpoint listy layoutów jest dostępny dla KAŻDEGO
 * zalogowanego usera tenanta (layouty to config tenant-global, nie dane rekordu),
 * więc wybór widoku formularza działa dla zwykłych użytkowników. Strona
 * wywołująca renderuje komponent bezwarunkowo — sam chowa się, gdy po filtrze
 * roli nie ma czego przełączać.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Dropdown } from '@/components/Dropdown';
import { type LayoutConfig } from '@/components/form/RecursiveFormRenderer';
import { api } from '@/lib/api';
import { isAlreadyNewShape, migrateLayoutToRecursive } from '@/lib/migrate-layout-to-recursive';

// ─── Typy ───────────────────────────────────────────────────────────────────

export interface SystemModuleFormLayoutRow {
  id: string;
  name: string;
  isDefault: boolean;
  /** Zawsze w NOWYM kształcie (migrowane w locie, patrz nagłówek pliku). */
  layoutConfig: LayoutConfig;
  /** UUID-y ról z dostępem do layoutu. Puste = uniwersalny (wszyscy). */
  roleIds: string[];
}

interface RawLayoutRow {
  id: string;
  name: string;
  isDefault: boolean;
  layoutConfig: unknown;
  roleIds: string[];
}

interface LayoutsListResponse {
  layouts: RawLayoutRow[];
}

// ─── Fetch współdzielony (picker + strona-caller) ─────────────────────────

/**
 * Lista layoutów typu 'form' modułu systemowego. Klucz React Query dzielony
 * między `DocumentLayoutPicker` (renderuje opcje) i stroną-callerem (rozwiązuje
 * `layoutConfig` wybranej pozycji do `RecursiveFormRenderer` / propa
 * `layoutConfigOverride` faktur) — jedno zapytanie sieciowe, dwóch konsumentów
 * tego samego cache'a.
 *
 * Bez bramki uprawnienia: lista layoutów jest dostępna dla każdego zalogowanego
 * usera tenanta, więc fetch idzie zawsze (tylko gdy jest `moduleSlug`).
 */
export function useSystemModuleFormLayouts(moduleSlug: string) {
  return useQuery({
    queryKey: ['system-module-form-layouts', moduleSlug],
    queryFn: async () => {
      const res = await api.get<LayoutsListResponse>(
        `/system-modules/${moduleSlug}/layouts?type=form`,
      );
      return res.data.layouts.map((row): SystemModuleFormLayoutRow => {
        const layoutConfig = isAlreadyNewShape(row.layoutConfig)
          ? (row.layoutConfig as LayoutConfig)
          : (migrateLayoutToRecursive(row.layoutConfig) as LayoutConfig);
        return {
          id: row.id,
          name: row.name,
          isDefault: row.isDefault,
          roleIds: row.roleIds,
          layoutConfig,
        };
      });
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!moduleSlug,
    retry: false,
  });
}

/**
 * Filtr produktowy (patrz nagłówek pliku) — uniwersalne + moja rola + domyślny;
 * super-user widzi wszystko.
 */
export function filterFormLayoutsForUser(
  layouts: SystemModuleFormLayoutRow[],
  opts: { myRoleId: string | null; isSuperUser: boolean },
): SystemModuleFormLayoutRow[] {
  if (opts.isSuperUser) return layouts;
  return layouts.filter(
    (l) =>
      l.isDefault ||
      l.roleIds.length === 0 ||
      (opts.myRoleId != null && l.roleIds.includes(opts.myRoleId)),
  );
}

// ─── Komponent ──────────────────────────────────────────────────────────────

export interface DocumentLayoutPickerProps {
  moduleSlug: string;
  /** `null` = brak overridu (używany layout aktywny/domyślny strony). */
  value: string | null;
  onChange: (layoutId: string | null) => void;
  myRoleId: string | null;
  isSuperUser: boolean;
  className?: string;
}

export function DocumentLayoutPicker({
  moduleSlug,
  value,
  onChange,
  myRoleId,
  isSuperUser,
  className,
}: DocumentLayoutPickerProps) {
  const { t } = useTranslation();
  const { data: layouts } = useSystemModuleFormLayouts(moduleSlug);

  const options = useMemo(
    () => filterFormLayoutsForUser(layouts ?? [], { myRoleId, isSuperUser }),
    [layouts, myRoleId, isSuperUser],
  );

  if (options.length <= 1) return null;

  return (
    <div className={['inline-flex items-center gap-1.5', className ?? ''].join(' ')}>
      <Dropdown
        value={value ?? ''}
        onChange={(v) => onChange(v || null)}
        aria-label={t('tenant.documents.layoutPicker.ariaLabel')}
        data-testid="document-layout-picker"
        options={[
          { value: '', label: t('tenant.documents.layoutPicker.activeOption') },
          ...options.map((l) => ({ value: l.id, label: l.name })),
        ]}
      />
    </div>
  );
}
